/**
 * useAgentModes — TanStack Query CRUD + version history for Agent Modes.
 *
 * Ports v1 ``ando-frontend/app/hooks/useAgentModes.ts`` +
 * ``ando-frontend/app/api/agentModes.ts`` call sites. v2 lets TanStack Query
 * own dedup / cache / refetch.
 *
 * BE source: ``ando-agent/src/api/v1/endpoints/agent_modes.py``.
 *
 * Capability gate
 * ---------------
 * Every endpoint here is gated by the ``"modes"`` capability (license
 * assignment). The listing query is short-circuited via
 * ``useHasCapability("modes")`` so users without the capability never
 * see a 403 — mutations would also be rejected by the BE.
 *
 * Versioning + rollback
 * ---------------------
 * Mode metadata (name/description/icon) → ``PATCH /agent-modes/{id}``.
 * Mode body (system prompt + default_config) → ``POST /agent-modes/{id}/versions``
 * which appends a new version and flips ``current_version_id``.
 * Rollback → ``POST /agent-modes/{id}/rollback`` with a previous version id.
 *
 * Note: this hook owns BOTH the studio drawer's list query and the
 * settings page CRUD. The query key ``["agent-modes"]`` is reused so the
 * drawer auto-refreshes on every mutation here.
 */

import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AgentMode,
  type AgentModeVersion,
  type CreateAgentModeRequest,
  type CreateAgentModeVersionRequest,
  type UpdateAgentModeMetadataRequest,
  createAgentMode,
  createAgentModeVersion,
  deleteAgentMode,
  listAgentModeVersions,
  listAgentModes,
  rollbackAgentMode,
  updateAgentModeMetadata,
} from "@ando/core";

import { useHasCapability } from "@/features/capabilities";

/**
 * Single query key used by every Agent Modes consumer (settings page, the
 * studio drawer, the chat header mode selector). Sharing keeps mutations
 * here automatically refreshing the drawer.
 */
export const agentModesQueryKey = ["agent-modes"] as const;

export const agentModeVersionsQueryKey = (modeId: string) =>
  ["agent-modes", modeId, "versions"] as const;

export interface UseAgentModesResult {
  readonly modes: AgentMode[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  /** ``false`` when the ``"modes"`` capability isn't granted. */
  readonly hasAccess: boolean;
  readonly refetch: () => Promise<unknown>;
  /** Underlying TanStack Query result for power-users / tests. */
  readonly query: UseQueryResult<AgentMode[]>;
}

/**
 * Listing hook — gated on the ``"modes"`` capability. Returns the empty
 * array (rather than ``undefined``) while loading so consumers can iterate
 * without a null-guard.
 */
export function useAgentModes(): UseAgentModesResult {
  const hasAccess = useHasCapability("modes");

  const query = useQuery<AgentMode[]>({
    queryKey: agentModesQueryKey,
    queryFn: listAgentModes,
    enabled: hasAccess,
    // Modes change rarely — 5 min stale keeps the multiple consumers
    // (settings page + drawer + chat header) from each refetching on mount.
    staleTime: 5 * 60_000,
  });

  return {
    modes: query.data ?? [],
    isLoading: hasAccess ? query.isLoading : false,
    isError: query.isError,
    error: query.error,
    hasAccess,
    refetch: () => query.refetch(),
    query,
  };
}

/**
 * Version history hook — fires only when ``modeId`` is set (i.e. the
 * history dialog is open). DESC by ``version_number`` so v(N) sits at
 * the top. Capability gate is enforced by the listing endpoint upstream
 * but we also short-circuit here to avoid useless 403s.
 */
export function useAgentModeVersions(modeId: string | null) {
  const hasAccess = useHasCapability("modes");
  return useQuery<AgentModeVersion[]>({
    queryKey: modeId ? agentModeVersionsQueryKey(modeId) : ["agent-modes", "_none_", "versions"],
    queryFn: () => listAgentModeVersions(modeId as string),
    enabled: hasAccess && modeId !== null,
    staleTime: 60_000,
  });
}

export function useCreateAgentMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAgentModeRequest) => createAgentMode(data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentModesQueryKey });
    },
  });
}

export function useUpdateAgentModeMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      modeId,
      data,
    }: {
      modeId: string;
      data: UpdateAgentModeMetadataRequest;
    }) => updateAgentModeMetadata(modeId, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentModesQueryKey });
    },
  });
}

export function useCreateAgentModeVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      modeId,
      data,
    }: {
      modeId: string;
      data: CreateAgentModeVersionRequest;
    }) => createAgentModeVersion(modeId, data),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: agentModesQueryKey });
      void qc.invalidateQueries({
        queryKey: agentModeVersionsQueryKey(vars.modeId),
      });
    },
  });
}

export function useDeleteAgentMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modeId: string) => deleteAgentMode(modeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: agentModesQueryKey });
    },
  });
}

export function useRollbackAgentMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      modeId,
      targetVersionId,
    }: {
      modeId: string;
      targetVersionId: string;
    }) => rollbackAgentMode(modeId, targetVersionId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: agentModesQueryKey });
      void qc.invalidateQueries({
        queryKey: agentModeVersionsQueryKey(vars.modeId),
      });
    },
  });
}
